import type { ThemeColors, Theme } from './types'

// ── Font Map (exported for use in theme cards) ────────────────────────────────
export const THEME_FONTS: Record<string, { family: string; name: string }> = {
  ember:        { family: "'Inter', system-ui, sans-serif", name: 'Inter' },
  pacific:      { family: "'Rajdhani', sans-serif", name: 'Rajdhani' },
  forest:       { family: "'Cabin', sans-serif", name: 'Cabin' },
  sierra:       { family: "'Josefin Sans', sans-serif", name: 'Josefin Sans' },
  arctic:       { family: "'Exo 2', sans-serif", name: 'Exo 2' },
  midnight:     { family: "'Quicksand', sans-serif", name: 'Quicksand' },
  barbie:       { family: "'Pacifico', cursive", name: 'Pacifico' },
  rainbow:      { family: "'Nunito', sans-serif", name: 'Nunito' },
  douchebag:    { family: "'Anton', sans-serif", name: 'Anton' },
  prepper:      { family: "'Courier Prime', monospace", name: 'Courier Prime' },
  patriot:      { family: "'Instrument Serif', 'Garamond', serif", name: 'Instrument Serif' },
  cowboy:       { family: "'Rye', cursive", name: 'Rye' },
  grandma:      { family: "'Lato', sans-serif", name: 'Lato' },
  surfer:       { family: "'Comfortaa', cursive", name: 'Comfortaa' },
  elgaucho:     { family: "'Righteous', cursive", name: 'Righteous' },
  overcast:     { family: "'Source Sans 3', sans-serif", name: 'Source Sans 3' },
  hippie:       { family: "'Boogaloo', cursive", name: 'Boogaloo' },
  doc:          { family: "'IBM Plex Sans', sans-serif", name: 'IBM Plex Sans' },
  cyborg:       { family: "'Share Tech Mono', monospace", name: 'Share Tech Mono' },
  terminal:     { family: "'VT323', monospace", name: 'VT323' },
  neon:         { family: "'Orbitron', sans-serif", name: 'Orbitron' },
  pirate:       { family: "'Pirata One', cursive", name: 'Pirata One' },
  area51:       { family: "'Aldrich', sans-serif", name: 'Aldrich' },
  goth:         { family: "'Cinzel', serif", name: 'Cinzel' },
}

// ── Presets ───────────────────────────────────────────────────────────────────

export const THEME_PRESETS: Record<string, { label: string; description: string; colors: ThemeColors }> = {
  ember: {
    label: '🔥 Cal Fire',
    description: 'Dark with red accents. Keep California safe.',
    colors: {
      primary: '#dc2626',
      primaryHover: '#b91c1c',
      secondary: '#1e40af',
      accent: '#d97706',
      headerBg: '#0f1523',
      cardBg: '#111827',
      pageBg: '#030712',
      sidebarBg: '#0a0a0a',
      text: '#f3f4f6',
      textMuted: '#9ca3af',
      border: '#1f2937',
      success: '#16a34a',
      warning: '#d97706',
      danger: '#dc2626',
    },
  },
  pacific: {
    label: 'Pacific',
    description: 'Dark navy with ocean blue accents. Maritime feel.',
    colors: {
      primary: '#2563eb',
      primaryHover: '#1d4ed8',
      secondary: '#0ea5e9',
      accent: '#38bdf8',
      headerBg: '#0c1a36',
      cardBg: '#0f172a',
      pageBg: '#020617',
      sidebarBg: '#020617',
      text: '#f1f5f9',
      textMuted: '#94a3b8',
      border: '#1e3a5f',
      success: '#16a34a',
      warning: '#d97706',
      danger: '#dc2626',
    },
  },
  forest: {
    label: '🌲 Forest Service',
    description: 'Green & gold. Shield up.',
    colors: {
      primary: '#fbbf24',
      primaryHover: '#f59e0b',
      secondary: '#16a34a',
      accent: '#84cc16',
      headerBg: '#0a3d2e',
      cardBg: '#052e16',
      pageBg: '#022c22',
      sidebarBg: '#011a14',
      text: '#f0fdf4',
      textMuted: '#86efac',
      border: '#14532d',
      success: '#16a34a',
      warning: '#fbbf24',
      danger: '#dc2626',
    },
  },
  sierra: {
    label: 'Sierra',
    description: 'Warm earth tones. Desert/mountain operations.',
    colors: {
      primary: '#d97706',
      primaryHover: '#b45309',
      secondary: '#b45309',
      accent: '#f59e0b',
      headerBg: '#2a2420',
      cardBg: '#292524',
      pageBg: '#1c1917',
      sidebarBg: '#0c0a09',
      text: '#fafaf9',
      textMuted: '#a8a29e',
      border: '#44403c',
      success: '#16a34a',
      warning: '#d97706',
      danger: '#dc2626',
    },
  },
  arctic: {
    label: 'Arctic',
    description: 'Cool grays with ice blue. Clean, clinical feel.',
    colors: {
      primary: '#06b6d4',
      primaryHover: '#0891b2',
      secondary: '#8b5cf6',
      accent: '#67e8f9',
      headerBg: '#232323',
      cardBg: '#1c1c1c',
      pageBg: '#111111',
      sidebarBg: '#0a0a0a',
      text: '#ededed',
      textMuted: '#8a8a8a',
      border: '#2e2e2e',
      success: '#16a34a',
      warning: '#d97706',
      danger: '#dc2626',
    },
  },
  midnight: {
    label: 'Midnight',
    description: 'Deep purple/indigo. Modern, sleek.',
    colors: {
      primary: '#7c3aed',
      primaryHover: '#6d28d9',
      secondary: '#a855f7',
      accent: '#c084fc',
      headerBg: '#171238',
      cardBg: '#1a1535',
      pageBg: '#0c0a1d',
      sidebarBg: '#080618',
      text: '#faf5ff',
      textMuted: '#a78bfa',
      border: '#312e81',
      success: '#16a34a',
      warning: '#d97706',
      danger: '#dc2626',
    },
  },
  barbie: {
    label: '💖 Barbie',
    description: 'Life in plastic, it\'s fantastic',
    colors: {
      primary: '#ff69b4',
      primaryHover: '#ff1493',
      secondary: '#ff85c8',
      accent: '#ff00ff',
      headerBg: '#30001e',
      cardBg: '#2d0020',
      pageBg: '#1a0011',
      sidebarBg: '#12000b',
      text: '#ffeef8',
      textMuted: '#ff85c8',
      border: '#4a0033',
      success: '#ff69b4',
      warning: '#ff85c8',
      danger: '#ff1493',
    },
  },
  rainbow: {
    label: '🌈 Rainbow',
    description: 'Taste the rainbow',
    colors: {
      primary: '#ff0000',
      primaryHover: '#cc0000',
      secondary: '#ff8800',
      accent: '#9b59b6',
      headerBg: '#ffe8d6',
      cardBg: '#fff5ee',
      pageBg: '#fffaf5',
      sidebarBg: '#ffe0cc',
      text: '#2d1b0e',
      textMuted: '#8b5e3c',
      border: '#f0d0b8',
      success: '#27ae60',
      warning: '#f1c40f',
      danger: '#e74c3c',
    },
  },
  douchebag: {
    label: '🔥 Douchebag',
    description: 'Affliction tee energy',
    colors: {
      primary: '#ff6600',
      primaryHover: '#e55c00',
      secondary: '#ff8533',
      accent: '#ffad33',
      headerBg: '#1a1200',
      cardBg: '#141414',
      pageBg: '#0a0a0a',
      sidebarBg: '#050505',
      text: '#ff8533',
      textMuted: '#995200',
      border: '#2a1a00',
      success: '#ff6600',
      warning: '#ffad33',
      danger: '#ff3300',
    },
  },
  prepper: {
    label: '🪖 Prepper',
    description: 'Blend in. Stay ready.',
    colors: {
      primary: '#4b5320',
      primaryHover: '#3a4119',
      secondary: '#6b7a3d',
      accent: '#8b7355',
      headerBg: '#262a1e',
      cardBg: '#2a2d22',
      pageBg: '#1a1c14',
      sidebarBg: '#13150f',
      text: '#d4d4aa',
      textMuted: '#8b8b6e',
      border: '#3d4030',
      success: '#4b5320',
      warning: '#8b7355',
      danger: '#8b0000',
    },
  },
  patriot: {
    label: '🇺🇸 Patriot',
    description: "'Murica!",
    colors: {
      primary: '#b22234',
      primaryHover: '#8b1a29',
      secondary: '#3c3b6e',
      accent: '#c9a84c',
      headerBg: '#e8ecf4',
      cardBg: '#f5f5f5',
      pageBg: '#ffffff',
      sidebarBg: '#2a2a5a',
      text: '#1a1a2e',
      textMuted: '#5c5c7a',
      border: '#d4d4d8',
      success: '#16a34a',
      warning: '#d97706',
      danger: '#b22234',
    },
  },
  cowboy: {
    label: '🤠 Cowboy',
    description: 'Saddle up, partner',
    colors: {
      primary: '#c17f3e',
      primaryHover: '#a3682f',
      secondary: '#8b6839',
      accent: '#d4a95a',
      headerBg: '#2a1f14',
      cardBg: '#32251a',
      pageBg: '#1e150e',
      sidebarBg: '#1a120b',
      text: '#e8d5b5',
      textMuted: '#9c8569',
      border: '#4a3728',
      success: '#6b8e3a',
      warning: '#c17f3e',
      danger: '#8b2500',
    },
  },
  grandma: {
    label: '🌸 Grandma',
    description: 'Smells like lavender and cookies',
    colors: {
      primary: '#c24b7a',
      primaryHover: '#a33d65',
      secondary: '#d4869c',
      accent: '#7aa37a',
      headerBg: '#f5d8e3',
      cardBg: '#fceef2',
      pageBg: '#fff5f8',
      sidebarBg: '#f5d5e0',
      text: '#3d1a2a',
      textMuted: '#8b5a6e',
      border: '#e8b8ca',
      success: '#7aa37a',
      warning: '#d4a017',
      danger: '#c24b7a',
    },
  },
  surfer: {
    label: '🏖️ Surfer',
    description: 'Hang loose, brah',
    colors: {
      primary: '#8b6914',
      primaryHover: '#705410',
      secondary: '#6b9e9e',
      accent: '#c2a366',
      headerBg: '#e8dfc4',
      cardBg: '#f0ead4',
      pageBg: '#faf5eb',
      sidebarBg: '#ddd4b8',
      text: '#3d3520',
      textMuted: '#8b7e5e',
      border: '#d4c9a8',
      success: '#6b9e9e',
      warning: '#c2a366',
      danger: '#b85c3a',
    },
  },
  elgaucho: {
    label: '🍊 El Gaucho',
    description: 'Zesty with a kick',
    colors: {
      primary: '#e8630a',
      primaryHover: '#c75308',
      secondary: '#84b818',
      accent: '#ff9e1a',
      headerBg: '#f5e4c8',
      cardBg: '#fff8f0',
      pageBg: '#fffefa',
      sidebarBg: '#f5e0c0',
      text: '#2d1a05',
      textMuted: '#8b6530',
      border: '#e8d0a8',
      success: '#84b818',
      warning: '#ff9e1a',
      danger: '#e8630a',
    },
  },
  overcast: {
    label: '☁️ Overcast',
    description: 'Cool, calm, collected',
    colors: {
      primary: '#4a6fa5',
      primaryHover: '#3b5a87',
      secondary: '#6b8cba',
      accent: '#8facc8',
      headerBg: '#d5d9e0',
      cardBg: '#e8eaed',
      pageBg: '#f0f2f5',
      sidebarBg: '#d8dce2',
      text: '#1a2433',
      textMuted: '#5a6a7a',
      border: '#c8cdd5',
      success: '#2e8b57',
      warning: '#b8860b',
      danger: '#c0392b',
    },
  },
  hippie: {
    label: '🌿 Hippie',
    description: 'Peace, love & good vibes',
    colors: {
      primary: '#5f8a5f',
      primaryHover: '#4a7a4a',
      secondary: '#8faa5f',
      accent: '#c2b280',
      headerBg: '#e2eacc',
      cardBg: '#f0f4e8',
      pageBg: '#f9faf5',
      sidebarBg: '#dde5cc',
      text: '#2a3320',
      textMuted: '#6b7a55',
      border: '#c8d4a8',
      success: '#5f8a5f',
      warning: '#c2b280',
      danger: '#a05a3c',
    },
  },
  doc: {
    label: '🏥 Doc',
    description: 'Scrub up, look sharp',
    colors: {
      primary: '#0d9488',
      primaryHover: '#0a7a70',
      secondary: '#475569',
      accent: '#06b6d4',
      headerBg: '#e6f0ef',
      cardBg: '#f8fafc',
      pageBg: '#ffffff',
      sidebarBg: '#0f172a',
      text: '#0f172a',
      textMuted: '#64748b',
      border: '#e2e8f0',
      success: '#0d9488',
      warning: '#d97706',
      danger: '#dc2626',
    },
  },
  cyborg: {
    label: '🤖 Cyborg',
    description: 'Machine learning intensifies',
    colors: {
      primary: '#00ff41',
      primaryHover: '#00cc33',
      secondary: '#0066ff',
      accent: '#00ffff',
      headerBg: '#0a1a0f',
      cardBg: '#0d1117',
      pageBg: '#0a0a0a',
      sidebarBg: '#050a05',
      text: '#00ff41',
      textMuted: '#00aa2a',
      border: '#00ff4133',
      success: '#00ff41',
      warning: '#ffff00',
      danger: '#ff0000',
    },
  },
  terminal: {
    label: '⌨️ Terminal',
    description: 'sudo make me a sandwich',
    colors: {
      primary: '#ff6600',
      primaryHover: '#cc5200',
      secondary: '#ffaa00',
      accent: '#ff8800',
      headerBg: '#1a1200',
      cardBg: '#141414',
      pageBg: '#0c0c0c',
      sidebarBg: '#080808',
      text: '#ff6600',
      textMuted: '#994400',
      border: '#ff660033',
      success: '#00ff00',
      warning: '#ffff00',
      danger: '#ff0000',
    },
  },
  neon: {
    label: '💠 Neon',
    description: 'Synthwave vibes only',
    colors: {
      primary: '#ff00ff',
      primaryHover: '#cc00cc',
      secondary: '#00ffff',
      accent: '#ff00aa',
      headerBg: '#1c0030',
      cardBg: '#1a0028',
      pageBg: '#0d0014',
      sidebarBg: '#08000f',
      text: '#ffffff',
      textMuted: '#cc88ff',
      border: '#ff00ff33',
      success: '#00ffaa',
      warning: '#ffff00',
      danger: '#ff0055',
    },
  },
  pirate: {
    label: '☠️ Pirate',
    description: 'Yarr, dead men tell no tales',
    colors: {
      primary: '#c9a84c',
      primaryHover: '#a88a3a',
      secondary: '#4a7c8f',
      accent: '#d4a937',
      headerBg: '#1a1410',
      cardBg: '#241c15',
      pageBg: '#120e0a',
      sidebarBg: '#0d0a07',
      text: '#d9c9a8',
      textMuted: '#8a7a5e',
      border: '#3a2e22',
      success: '#2e8b57',
      warning: '#c9a84c',
      danger: '#8b1a1a',
    },
  },
  area51: {
    label: '👽 Area 51',
    description: 'The truth is out there',
    colors: {
      primary: '#39ff14',
      primaryHover: '#2ecc0f',
      secondary: '#00e5ff',
      accent: '#76ff03',
      headerBg: '#0a0e07',
      cardBg: '#0d120a',
      pageBg: '#050805',
      sidebarBg: '#030503',
      text: '#c8ffc8',
      textMuted: '#4a8a4a',
      border: '#1a3a1a',
      success: '#39ff14',
      warning: '#ffeb3b',
      danger: '#ff1744',
    },
  },
  goth: {
    label: '🖤 Goth',
    description: 'The void stares back',
    colors: {
      primary: '#8b0000',
      primaryHover: '#a00000',
      secondary: '#4a0028',
      accent: '#c41e3a',
      headerBg: '#0a0a0a',
      cardBg: '#0f0f0f',
      pageBg: '#050505',
      sidebarBg: '#030303',
      text: '#b8b8b8',
      textMuted: '#555555',
      border: '#1a1a1a',
      success: '#2e4a2e',
      warning: '#8b6508',
      danger: '#8b0000',
    },
  },
}

export const DEFAULT_THEME: Theme = {
  preset: 'ember',
  colors: THEME_PRESETS.ember.colors,
  backgroundImage: null,
  backgroundOpacity: 0.05,
  cardTransparency: 1.0,
  borderRadius: '0.75rem',
  listStyle: 'card',
}
